import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Ticket, Trash2, Loader2, Plus, Copy, Check, TrendingUp, Users, Coins, UserPlus, Target } from "lucide-react";
import { toast } from "sonner";

export function AdminVouchers() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", credits: 100, max_uses: 1, expires_at: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/vouchers");
      setRows(r.data.vouchers || []);
      setTotals(r.data.totals || {});
      setCampaigns(r.data.campaigns || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/vouchers", {
        code: form.code.trim().toUpperCase(),
        credits: Number(form.credits),
        max_uses: Number(form.max_uses),
        expires_at: form.expires_at || null,
        note: form.note || null,
      });
      toast.success("Kupon létrehozva");
      setForm({ code: "", credits: 100, max_uses: 1, expires_at: "", note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sikertelen létrehozás");
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Törlöd a "${v.code}" kupont?`)) return;
    try { await api.delete(`/admin/vouchers/${v.id}`); load(); } catch { toast.error("Sikertelen törlés"); }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div data-testid="admin-vouchers" className="space-y-6">
      {/* Analytics summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vouchers-analytics-totals">
        <Stat icon={Ticket} label="Kuponok" value={totals.vouchers ?? 0} color="text-electric" />
        <Stat icon={TrendingUp} label="Beváltások" value={totals.redemptions ?? 0} color="text-amber-400" testid="totals-redemptions" />
        <Stat icon={Coins} label="Adott kredit" value={totals.credits_given ?? 0} color="text-emerald-400" testid="totals-credits" />
        <Stat icon={UserPlus} label="Új regisztráció (≤7 nap)" value={totals.new_signups ?? 0} color="text-fuchsia-400" testid="totals-new-signups" />
      </div>

      {/* Kampányok (note szerint csoportosítva) */}
      {campaigns.length > 0 && (
        <div data-testid="vouchers-campaigns" className="glass rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} />
            <h3 className="font-heading text-lg">Kampányok (kupon `note` szerint)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {campaigns.map((c) => {
              const conv = c.redemptions > 0 ? Math.round((c.new_signups / c.redemptions) * 100) : 0;
              return (
                <div key={c.name} data-testid={`campaign-${c.name}`} className="bg-white/[0.03] rounded-xl p-4 border border-white/5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wide text-slate-500">Kampány</div>
                      <div className="font-heading text-lg text-fuchsia-300 truncate">{c.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-slate-500">Konverzió</div>
                      <div className="text-sm text-emerald-400 font-medium" data-testid={`campaign-conv-${c.name}`}>{conv}%</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <MicroStat label="Kuponok" value={c.voucher_count} testid={`campaign-vouchers-${c.name}`} />
                    <MicroStat label="Beváltás" value={c.redemptions} testid={`campaign-redemptions-${c.name}`} />
                    <MicroStat label="Új user" value={c.new_signups} highlight testid={`campaign-newsignups-${c.name}`} />
                    <MicroStat label="Kredit" value={c.credits_given} testid={`campaign-credits-${c.name}`} />
                  </div>
                  <div className="mt-2 text-[10px] text-slate-500 font-mono truncate">
                    {c.codes.slice(0, 3).join(", ")}{c.codes.length > 3 && ` +${c.codes.length - 3}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <form onSubmit={create} className="glass rounded-2xl p-5 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400 block mb-1">Kupon kód</label>
          <input data-testid="voucher-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="KARACSONY2026" className="w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm font-mono focus:border-electric outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400 block mb-1">Kredit</label>
          <input data-testid="voucher-credits" type="number" min="1" required value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} className="w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm focus:border-electric outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400 block mb-1">Max felhasználás</label>
          <input data-testid="voucher-max-uses" type="number" min="1" required value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm focus:border-electric outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400 block mb-1">Lejárat</label>
          <input data-testid="voucher-expires" type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm focus:border-electric outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400 block mb-1">Megjegyzés / kampány</label>
          <input data-testid="voucher-note" value={form.note} maxLength={200} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Karácsonyi kampány" className="w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm focus:border-electric outline-none" />
        </div>
        <div className="md:col-span-5">
          <button data-testid="voucher-create-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Létrehozás
          </button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-electric animate-spin" /></div>
      ) : rows.length === 0 ? (
        <p data-testid="vouchers-empty" className="text-slate-400 text-sm">Nincs létrehozott kupon.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((v) => {
            const a = v.analytics || {};
            const conversion = a.redemptions > 0 ? Math.round((a.new_signups / a.redemptions) * 100) : 0;
            return (
              <div key={v.id} data-testid={`voucher-row-${v.id}`} className="glass rounded-2xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                      <Ticket className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-lg" data-testid={`voucher-code-${v.id}`}>{v.code}</span>
                        <button onClick={() => copy(v.code)} className="text-xs text-slate-400 hover:text-electric inline-flex items-center gap-1">
                          {copied === v.code ? <><Check className="w-3 h-3" /> Másolva</> : <><Copy className="w-3 h-3" /> Másol</>}
                        </button>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        <b className="text-emerald-400">+{v.credits} kredit</b> · <span data-testid={`voucher-uses-${v.id}`}>{v.uses}/{v.max_uses}</span> beváltva
                        {v.expires_at && <> · lejár: {new Date(v.expires_at).toLocaleDateString("hu-HU")}</>}
                        {v.note && <> · <span className="text-slate-300">{v.note}</span></>}
                      </div>
                    </div>
                  </div>
                  <button data-testid={`voucher-delete-${v.id}`} onClick={() => remove(v)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {/* Analytics per voucher */}
                <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid={`voucher-analytics-${v.id}`}>
                  <MicroStat label="Beváltás" value={a.redemptions ?? 0} testid={`voucher-analytics-redemptions-${v.id}`} />
                  <MicroStat label="Egyedi user" value={a.unique_users ?? 0} testid={`voucher-analytics-unique-${v.id}`} />
                  <MicroStat label="Új regisztráció" value={a.new_signups ?? 0} highlight testid={`voucher-analytics-newsignups-${v.id}`} />
                  <MicroStat label="Adott kredit" value={a.credits_given ?? 0} testid={`voucher-analytics-credits-${v.id}`} />
                  <MicroStat label="Reg. konverzió" value={`${conversion}%`} testid={`voucher-analytics-conversion-${v.id}`} />
                </div>
                {(a.first_redeemed_at || a.last_redeemed_at) && (
                  <div className="mt-2 text-[11px] text-slate-500">
                    {a.first_redeemed_at && <>Első: {new Date(a.first_redeemed_at).toLocaleString("hu-HU")}</>}
                    {a.first_redeemed_at && a.last_redeemed_at && a.first_redeemed_at !== a.last_redeemed_at && <> · </>}
                    {a.last_redeemed_at && a.first_redeemed_at !== a.last_redeemed_at && <>Utolsó: {new Date(a.last_redeemed_at).toLocaleString("hu-HU")}</>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl bg-white/[0.03] flex items-center justify-center ${color}`}>
      <Icon className="w-4 h-4" strokeWidth={1.5} />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`font-heading text-xl font-light ${color}`}>{value}</div>
    </div>
  </div>
);

const MicroStat = ({ label, value, highlight, testid }) => (
  <div className="rounded-lg bg-white/[0.02] px-2.5 py-1.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`font-medium ${highlight ? "text-fuchsia-400" : "text-slate-200"}`}>{value}</div>
  </div>
);
